import { useEffect, useState } from 'react';
import type { WorkOrderSearchMatch } from '@tcg/shared';
import { ApiClientError } from '../lib/api';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Spinner } from './ui';

/**
 * Resolve a work-order NUMBER to a single WorkOrderId via search, shared by the
 * Submit Quote and Documents flows:
 *   - 0 matches → "not found" block
 *   - exactly 1 → auto-select (onResolved fires immediately, no picker shown)
 *   - >1 → a selectable list (number, customer, status, type, SLA priority)
 * `search` is injected so each page points at its own endpoint.
 */
export function WorkOrderPicker({
  woNumber,
  search,
  onResolved,
  onReset,
}: {
  woNumber: string;
  search: () => Promise<WorkOrderSearchMatch[]>;
  onResolved: (workOrderId: number) => void;
  onReset?: () => void;
}) {
  const [state, setState] = useState<'loading' | 'list' | 'notfound' | 'error'>('loading');
  const [matches, setMatches] = useState<WorkOrderSearchMatch[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setErr(null);
    search()
      .then((ms) => {
        if (cancelled) return;
        if (ms.length === 0) {
          setState('notfound');
          return;
        }
        if (ms.length === 1) {
          onResolved(ms[0].workOrderId); // exactly one → skip the picker
          return;
        }
        setMatches(ms);
        setState('list');
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof ApiClientError ? e.message : 'Could not search work orders.');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
    // Re-run only when the searched number changes (search is a fresh closure each render).
  }, [woNumber]);

  if (state === 'loading') {
    return (
      <Card>
        <CardBody className="flex items-center gap-3 text-sm text-slate-500">
          <Spinner /> Searching work orders for <strong>{woNumber}</strong>…
        </CardBody>
      </Card>
    );
  }

  if (state === 'notfound') {
    return (
      <Banner tone="error">
        No work order found for <strong>{woNumber}</strong>. Check the number and try again.
        {onReset && (
          <>
            {' '}
            <button type="button" className="font-medium underline" onClick={onReset}>
              Edit number
            </button>
          </>
        )}
      </Banner>
    );
  }

  if (state === 'error') {
    return <Banner tone="error">{err}</Banner>;
  }

  return (
    <Card>
      <CardHeader
        title="Which work order?"
        subtitle={`${matches.length} work orders match ${woNumber}. Select the right one to continue.`}
        action={
          onReset ? (
            <Button variant="ghost" onClick={onReset}>
              ← Edit number
            </Button>
          ) : undefined
        }
      />
      <CardBody className="space-y-2">
        {matches.map((m) => (
          <button
            key={m.workOrderId}
            type="button"
            onClick={() => onResolved(m.workOrderId)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-accent-400 hover:bg-accent-50/40"
          >
            <div className="min-w-0">
              <div className="font-medium text-slate-800">
                {m.workOrderNumber}
                <span className="ml-1 text-xs font-normal text-slate-400">#{m.workOrderId}</span>
              </div>
              <div className="truncate text-xs text-slate-500">
                {m.customerName ?? 'Unknown customer'}
                {m.type ? ` · ${m.type}` : ''}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {m.priorityName && (
                <Badge tone={m.isEmergency ? 'red' : 'slate'}>
                  {m.isEmergency ? '🚨 ' : ''}
                  {m.priorityName}
                </Badge>
              )}
              {m.status && <Badge tone="slate">{m.status}</Badge>}
            </div>
          </button>
        ))}
      </CardBody>
    </Card>
  );
}
